import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { fmtNum, fmtRiyal } from "@/utils/toEnglishDigits";
import { ChartLineUp, Money, Users, Suitcase, TrendUp, User as UserIcon, UserCircle } from "@phosphor-icons/react";

const Kpi = ({ icon: Icon, label, value, tone = "gold", testId }) => {
  const c =
    tone === "gold" ? "text-primary"
    : tone === "green" ? "text-[#2E7D32]"
    : tone === "red" ? "text-[#F57F17]"
    : tone === "info" ? "text-[#38BDF8]"
    : "text-white";
  return (
    <div className="fadeup bg-card border border-primary/15 rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-white/60 text-sm">{label}</div>
        <Icon size={22} weight="duotone" className="text-primary" />
      </div>
      <div className={`text-2xl md:text-3xl font-bold mt-3 num ${c}`}>{value}</div>
    </div>
  );
};

export default function FinancePage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/summary");
      setSummary(data);
    } catch {
      toast.error("تعذر تحميل البيانات المالية");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sortedDrivers = useMemo(() => {
    const arr = [...(summary?.per_driver || [])];
    return arr.sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
  }, [summary]);

  const maxRevenue = sortedDrivers[0]?.revenue || 1;

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white">النظام المالي</h1>
          <p className="text-white/50 text-sm mt-1">تقارير تفصيلية وتسويات السائقين</p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : summary ? (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              <Kpi icon={Money} label="إجمالي الإيرادات" value={fmtRiyal(summary.total_revenue || 0)} tone="gold" testId="kpi-revenue" />
              <Kpi icon={Users} label="مدفوع للسائقين" value={fmtRiyal(summary.total_wages || 0)} tone="red" testId="kpi-wages" />
              {/* 2026-07 §AgentCommissionKpi — always visible, even when zero. */}
              <Kpi icon={UserCircle} label="عمولة المناديب" value={fmtRiyal(summary.total_commission || 0)} tone="info" testId="kpi-commission" />
              <Kpi icon={TrendUp} label="صافي ربح الشركة" value={fmtRiyal(summary.net_profit || 0)} tone="green" testId="kpi-profit" />
              <Kpi icon={Suitcase} label="إجمالي الرحلات" value={fmtNum(summary.total_bookings || 0)} tone="white" testId="kpi-trips" />
            </div>
            <div className="text-white/40 text-[11px] mb-6 text-center">
              محسوب تلقائياً: الإيرادات − أجور السائقين − عمولة المناديب.
            </div>

            {/* Per-driver revenue chart */}
            <div className="bg-card border border-primary/15 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <ChartLineUp size={22} weight="duotone" className="text-primary" />
                <h2 className="text-white font-bold text-lg">أرباح السائقين (حسب الإيرادات)</h2>
              </div>

              {sortedDrivers.length === 0 ? (
                <div className="text-center py-10 text-white/40">لا توجد بيانات</div>
              ) : (
                <div className="space-y-2.5">
                  {sortedDrivers.map((d, i) => {
                    const pct = Math.max(3, ((d.revenue || 0) / maxRevenue) * 100);
                    return (
                      <div key={d.driver_id} className="fadeup" style={{ animationDelay: `${Math.min(i, 15) * 30}ms` }} data-testid={`driver-perf-${d.driver_id}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <div className="flex items-center gap-2">
                            <UserIcon size={14} className="text-primary/60" />
                            <span className="text-white font-medium truncate max-w-[200px] md:max-w-[400px]">{d.driver_name || "—"}</span>
                            <span className="text-white/40 text-xs">·</span>
                            <span className="text-white/50 text-xs num">{fmtNum(d.trips)} رحلة</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-primary font-bold num">{fmtRiyal(d.revenue || 0)}</span>
                            {(d.wages || 0) > 0 && <span className="text-[#F57F17] text-xs num">أجرة: {fmtRiyal(d.wages)}</span>}
                          </div>
                        </div>
                        <div className="h-2.5 rounded-full bg-[#0B1A3A] overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              background: "linear-gradient(90deg, #C9A961 0%, #E1C484 100%)",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-16 text-white/40">لا توجد بيانات</div>
        )}
      </main>
    </div>
  );
}
